(ns ycweb.components.datatables.datatables
  (:require
   [clojure.set :as set]
   [rum.core :as rum]
   [util.transform :as trf]
   [ycweb.util.core :as yuc]
   [ycweb.util.date :as ud]
   [ycweb.components.forms :as forms]
   [util.string :as str]
   [ycweb.components.common :as cmn]
   [ycweb.components.grid :as grid]
   [inflections.core :as inflections]
   [ycweb.util.comms :as comms]
   [ycweb.components.datatables.buttons :as dt-buttons]
   [ycweb.components.datatables.select :as dt-select]
   [ycweb.components.pagination :as pagination]
   [ycweb.components.toaster :as toaster]))

;;Allow for user defined cursor path in the config
(def table-cursor-prefix :datatables)

(defn table-cursor
  "Get the correct namespaced datatable atom inside a global app-atom"
  ([app-atom]
   (let [action-atom (cmn/action-cursor app-atom)]
     (rum/cursor action-atom table-cursor-prefix)))
  ([app-atom cursor]
   (let [action-atom (cmn/action-cursor app-atom)]
     (rum/cursor-in action-atom [table-cursor-prefix cursor]))))

(defn index-columns [columns-defs]
  (->>
    columns-defs
    (map-indexed
      (fn [idx columns-def]
        (assoc columns-def :column-idx idx)))
    (vec)))

(defn group-columns [idx-columns]
  (let [has-groups (filter :group idx-columns)]
    (when (not (empty? has-groups))
      (->>
        (reduce
          (fn [acc column]
            (let [empty-group "empty-group"
                  existing-groups (filter (fn [[k v]] (not (str/starts-with? k empty-group))) acc)
                  empty-group-header (str empty-group "-" (count existing-groups))
                  existing-group (get acc (:group column))
                  existing-empty-group (get acc empty-group-header)]
              (cond
                existing-group
                (update-in acc [(:key existing-group) :colspan] inc)

                (yuc/presence (:group column))
                (assoc acc
                  (:group column)
                  {:key (:group column) :colspan 1 :group-idx (:column-idx column)})

                existing-empty-group
                (update-in acc [(:key existing-empty-group) :colspan] inc)

                :else
                (assoc acc
                  empty-group-header
                  {:key empty-group-header :colspan 1 :group-idx (:column-idx column)}))))
          {} idx-columns)
        (vals)
        (sort-by :group-idx)
        (vec)))))

(def btn-lookup
  {:refresh
   {:text "Refresh"
    :action :datatables/refresh
    :config {:mode :read}}})

(defn select-row [app-atom table-name row on-click-fn]
  (let [table-atom (table-cursor app-atom table-name)
        {:keys [row-id]} (:config @table-atom)
        current-row-id (get row row-id)]
    (dt-select/select table-atom current-row-id)
    (when on-click-fn
      (on-click-fn row))))

(defn data-atom [app-atom table-name {:keys [end-point cursor data-fn]}]
  (let [table-atom (table-cursor app-atom table-name)]
    (cond
      end-point
      (rum/cursor table-atom :data)

      cursor
      (rum/cursor-in app-atom cursor)

      ;;TODO: This data-fn may not be necessary but will leave it in for now! 23/02/2022
      data-fn
      (data-fn)

      :else
      (toaster/open-toast app-atom :error "Data source missing" {:closetime 5000}))))

(defn cell-data [row {:keys [edit-field] :as col} mode]
  (let [column-name (if (= mode :read) (:key col) (or edit-field (:key col)))

        value
        (cond
          (vector? column-name)
          (get-in row column-name)

          ;; TODO this hack is awful, but to avoid it we need to fix up the tables that have a namespaced keyword discrepancy between config and data.
          column-name
          (let [namespaced? (namespace column-name)
                field-name (if namespaced? (keyword (name column-name)) column-name)
                cell-val (or (get row field-name) (get row column-name))]
            cell-val)

          :else
          "")]
    value))

(rum/defc row-selector < rum/reactive [table-atom raw-data-atom select? idx-key]
  (cond
    (or
      (and (boolean? select?) select?)
      (= (:style select?) :single))
    (forms/radio "" #{idx-key}
      (rum/cursor-in table-atom [:select])
      {})

    :else
    [:label
     {:class (yuc/bem :checkbox-label [:inline])}
     (forms/checkbox-raw
       (rum/cursor-in raw-data-atom [idx-key :selected?])
       {:change-fn #(dt-select/select table-atom idx-key)})
     [:span.checkbox__graphic-wrapper [:span.checkbox__graphic]]]))

(defn get-logic-fn [table-fns column-def]
  (let [column-logic-key (get-in column-def [:render :logic-k])]
    (or
      (get (:column-fns table-fns) column-logic-key)
      (fn [_app-atom _row _col v] v))))

(rum/defc row-cell < rum/reactive
  [app-atom raw-data-atom table-name
   {:keys [default align-class col-classes style render options cell-edit cell-classes-k] :as col}
   column-fns row {:keys [row-id select? index has-selector-column? row-on-click]}]
  (let [table-atom (table-cursor app-atom table-name)
        {:keys [render-k logic-k]} render
        cell-val (cell-data row col :read)
        cell-classes-fn (get column-fns cell-classes-k)
        cell-edit-fn
        (when cell-edit
          (get column-fns cell-edit))

        idx-key (get row row-id)

        cell-classes
        (when cell-classes-k
          (cell-classes-fn row col))

        html-cell-classes
        (map name
          (vec
            (remove nil?
              (concat
                [(cond
                   align-class
                   align-class

                   (contains? #{:currency :number :tocurrency} (:type col))
                   :u-text-right

                   (contains? #{:selector :date} (:type col))
                   :u-text-center)]
                cell-classes
                col-classes))))

        render-fn (or (get column-fns render-k) (fn [_ _ _ cell-val] cell-val))
        logic-fn (or (get column-fns logic-k) (fn [_ _ _ cell-val] cell-val))

        typefn
        (case (:type col)
          :date
          (fn [value]
            (cond
              (and
                (yuc/presence value)
                (not= value (:default col)))
              (ud/format-date value (or (:dateformat col) "yyyy/MM/dd HH:mm"))

              (yuc/presence (:default col))
              (:default col)))
          :currency #(str "R" (yuc/number-format % 2))
          :percentage #(str (yuc/number-format (or % 0) 2) "%")
          ;;rename to 'fromcurrency' to align with the function name
          :tocurrency #(str "R" (yuc/currency-format % 2))
          :tohumancurrency #(str "R" (yuc/human-readable-currency %))
          :link
          (fn [[view link]]
            [:a
             (merge
               (when (:redirect? options)
                 {:target "_blank"})
               {:href link})
             view])
          :action
          (fn [[view action config]]
            [:a
             {:on-click #(grid/dispatch-action app-atom action config)}
             view])
          :number #(if (:decimals col) (yuc/number-format % (:decimals col)) (yuc/number-format %))
          :img
          (fn [[publicID fileName specs]]
            [:img
             {:class (yuc/bem :image [:full])
              :src
              (yuc/cloudinary
                publicID
                (or
                  specs
                  {:h 128
                   :q "auto"
                   :f "auto"}))
              :alt fileName}])
          :selector
          (fn []
            (row-selector table-atom raw-data-atom select? idx-key))
          identity)]
    [:td
     {:class html-cell-classes
      :style (or style {})
      :on-click
      (fn []
        (cond
          ;;row click logic moved to cell, as we can then have more control over
          ;; which parts of the row should be clickable..
          (and select? (not has-selector-column?) (not (:ignore-row-click options)))
          (select-row app-atom table-name row row-on-click)

          (and row-on-click (not (:ignore-row-click options)))
          #(row-on-click row))

        ;;inline editing
        (when (and cell-edit-fn (not (:editing? row)))
          (swap! table-atom update-in
            [:config :column-defs index] assoc :editing? true)
          (swap! raw-data-atom assoc-in [idx-key :editing?] true)))}
     (cond
       (and cell-edit-fn (:editing? row) (:editing? col))
       (let [edit-cell-val (logic-fn app-atom row col cell-val)]
         (cell-edit-fn app-atom (dissoc col :cell-edit) row edit-cell-val))

       cell-val
       (->>
         cell-val
         (logic-fn app-atom row col) ;; Uses the passed in key to look up or apply logic to result in your final value
         (typefn)
         (render-fn app-atom row col))

       :else
       (->>
         default
         (logic-fn app-atom row col) ;; Uses the passed in key to look up or apply logic to result in your final value
         (typefn)
         (render-fn app-atom row col)))]))

(rum/defc row-component < rum/reactive
  [app-atom
   {:keys [row-id select? table-name row-class-k row-on-click column-defs]}
   table-fns selected-row-ids raw-data-atom row]
  (let [current-row-id (get row row-id)
        row-class-fn (get-in table-fns [:config-fns row-class-k])
        column-fns (:column-fns table-fns)
        removed-hidden-column-defs (remove :default-hidden? column-defs)
        has-selector-column? (trf/toboolean (seq (filter #(= (:type %) :selector) column-defs)))]
    [:tr
     {:id current-row-id
      :class
      (remove nil?
        (concat
          [(when (and selected-row-ids (contains? selected-row-ids current-row-id))
             "selected-row highlight")]
          (when row-class-k
            (row-class-fn app-atom row))))}

     (map-indexed
       #(rum/with-key
          (row-cell app-atom raw-data-atom table-name %2 column-fns row
            {:row-id row-id :select? select? :index %1
             :has-selector-column? has-selector-column?
             :row-on-click row-on-click})
          (str %1 "-" (get %2 :title) "-" (get row row-id)))
       removed-hidden-column-defs)]))

(rum/defc group-header-comp < rum/reactive [col]
  [:th
   {:colSpan (:colspan col)
    :align "center"}
   [:div (when-not (str/starts-with? (:key col) "empty-group") (:key col))]])

(rum/defc header-comp < rum/reactive [sort-title-atom order-atom col]
  (let [order (rum/react order-atom)

        current
        (cond
          (not (= (rum/react sort-title-atom) (:title col)))
          :tablesorter-header

          (= order :desc)
          :tablesorter-headerDesc

          (= order :asc)
          :tablesorter-headerAsc)

        col-title
        (if (yuc/presence (:group col))
          (str (:group col) " " (:title col))
          (:title col))]

    [:th.tablesorter-header
     (yuc/maybe assoc
       {:align "center"
        :on-click
        #(do
           (if (= @sort-title-atom col-title)
             (swap! order-atom
               (fn [x]
                 (if (= x :desc)
                   :asc
                   :desc)))
             (reset! sort-title-atom col-title)))
        :class
        (remove nil?
          (concat
            [(when (:no-wrap col) :u-text-nowrap)]
            (yuc/bem current [])))
        :style {:user-select "none"}}
       :scope (:scope col))
     [:div.tablesorter-header-inner (:title col)]]))

(rum/defc footer-comp < rum/reactive [col]
  [:td
   (yuc/maybe assoc
     {:class
      [(cond
         (contains? #{:currency :number :tocurrency} (:type col))
         :u-text-right

         (contains? #{:selector :date} (:type col))
         :u-text-center)]
      :style {:user-select "none"}})
   [:div (:value col)]])

(rum/defc search-bar-text < rum/reactive [filters-atom col-title]
  (forms/input-raw ""
    (rum/cursor filters-atom col-title)
    {:class :tablesorter-filter}))

(rum/defc search-bar-select < rum/reactive [filters-atom data column-def col-title]
  (let [column-key (:key column-def)
        column-list (set (map #(get % column-key) data))
        default-title (inflections/pluralize nil (:title column-def))]
    (forms/select-raw
      (rum/cursor filters-atom col-title)
      (into
        [{:text (str "All " default-title) :value ""}]
        (map
          (fn [i]
            {:text i})
          column-list))
      {:small? true})))

(rum/defc search-bar < rum/reactive [filters-atom data column-def]
  (let [col-title
        (if (yuc/presence (:group column-def))
          (str (:group column-def) " " (:title column-def))
          (:title column-def))

        searchable-input
        (case (:searchable column-def)
          :select
          (search-bar-select filters-atom data column-def col-title)

          (search-bar-text filters-atom col-title))]
    [:td searchable-input]))

(defn search-table [data filters-atom column-defs]
  [(into
     [:tr.tablesorter-filter-row
      {:role "row"}]
     (map-indexed
       (fn [idx col-def]
         (rum/with-key
           (search-bar filters-atom data col-def)
           (str (:title col-def) "-" idx)))
       column-defs))])

(defn magic-operator [magic-filter]
  (let [one-char (str/subs magic-filter 0 1)
        two-char (str/subs magic-filter 0 2)
        str-count (count magic-filter)]
    (cond
      (= two-char ">=")
      {:selected-operator >= :selected-value (str/subs magic-filter 2 str-count)}

      (= two-char "<=")
      {:selected-operator <= :selected-value (str/subs magic-filter 2 str-count)}

      (= one-char "<")
      {:selected-operator < :selected-value (str/subs magic-filter 1 str-count)}

      (= one-char ">")
      {:selected-operator > :selected-value (str/subs magic-filter 1 str-count)}

      (= one-char "=")
      {:selected-operator = :selected-value (str/subs magic-filter 1 str-count)}

      (= one-char "!")
      {:selected-operator not :selected-value (str/subs magic-filter 1 str-count)}

      :else
      {:selected-value magic-filter})))

(defn filter-rows [app-atom table-fns filters columns-by-title data]
  #?(:cljs
     (cond
       (not
         (empty?
           (remove nil?
             (vals filters))))
       (filter
         (fn [row]
           (every?
             (fn [ftitle]
               (let [column-filter (get filters ftitle)

                     filter-column (get columns-by-title ftitle)

                     filter-column-key
                     (get filter-column :key)

                     column-logic-lookup
                     (get-in filter-column [:render :logic-k])

                     column-logic-fn
                     (or
                       (get-in table-fns [:column-fns column-logic-lookup])
                       (fn [_app-atom _row _col v] v))

                     row-value
                     (column-logic-fn
                       app-atom row filter-column-key (cell-data row {:key filter-column-key} :read))

                     {:keys [selected-operator selected-value]}
                     (magic-operator column-filter)

                     valid-numbers?
                     (and
                       (number? row-value)
                       (re-find #"^[0-9]\d*(\.\d+)?$" selected-value))]

                 (cond
                   (not (or valid-numbers? (nil? selected-operator)))
                   (selected-operator
                     (str/includes?
                       (str/lower (str row-value))
                       (str/lower (str selected-value))))

                   (and valid-numbers? selected-operator)
                   (selected-operator
                     row-value
                     (js/parseInt selected-value))

                   ;; Support pipe-separated OR values (e.g., "Active|Inactive|Running")
                   (str/includes? column-filter "|")
                   (let [or-values (str/split column-filter #"\|")
                         row-value-lower (str/lower (str row-value))]
                     (some
                       #(str/includes? row-value-lower (str/lower %))
                       or-values))

                   :else
                   (str/includes?
                     (str/lower (str row-value))
                     (str/lower (str column-filter))))))
             (keys filters)))
         data)

       :else
       data)))

(rum/defc table-raw < rum/reactive
  [app-atom
   {:keys
    [table-name row-id data-source
     column-defs options colgroup] :as tbl-config}
   table-fns]
  #?(:cljs
     (let [table-atom (table-cursor app-atom table-name)
           config-atom (rum/cursor table-atom :config)
           options-atom (rum/cursor config-atom :options)
           pagination-atom (rum/cursor options-atom :pagination)
           selected-row-ids (cmn/curact table-atom :select)

           removed-hidden-column-defs (remove :default-hidden? column-defs)

           filter-lookup (:filter options)
           filter-fn (get-in table-fns [:config-fns filter-lookup])

           raw-data-atom (data-atom app-atom table-name data-source)
           raw-data (vals (rum/react raw-data-atom))

           footer (cmn/curact config-atom :footer)
           footer-rows (:rows footer)

           data
           (if filter-fn
             (filter-fn app-atom raw-data)
             raw-data)

           sort-title-atom (rum/cursor options-atom :sort-title)
           order-atom (rum/cursor options-atom :order)

           filters-atom (rum/cursor options-atom :filters)
           filters (rum/react filters-atom)
           columns-by-title (yuc/index-by #(str/trim (str (:group %) " " (:title %))) column-defs)

           paging-fn
           (fn [data]
             (if (:paging? options)
               (pagination/current-page-data pagination-atom data)
               data))

           sorting-fn
           (fn [data]
             (let [sorting-title (rum/react sort-title-atom)
                   sorting-column (get columns-by-title sorting-title)
                   sorting-key (or (:sorting-key sorting-column) (:key sorting-column))

                   sort-ordering
                   (if
                     (= (rum/react order-atom) :desc)
                     reverse
                     identity)

                   column-logic-fn (get-logic-fn table-fns sorting-column)]
               ;;TODO when refactoring to namespaced keywords remove this coercing of keys
               (cond
                 (vector? sorting-key)
                 (sort-ordering
                   (sort-by
                     #(column-logic-fn app-atom % sorting-column (get-in % sorting-key))
                     data))

                 sorting-key
                 (sort-ordering
                   (sort-by
                     #(column-logic-fn app-atom % sorting-column
                        (or ;;TODO @Zak I really hate this dance
                          (get % (keyword (name sorting-key)))
                          (get % sorting-key)))
                     data))

                 :else data)))

           filtering-fn
           (fn [data]
             (filter-rows app-atom table-fns filters columns-by-title data))

           formatting-fn
           (partial
             map-indexed
             #(rum/with-key
                (row-component app-atom tbl-config table-fns selected-row-ids raw-data-atom %2)
                (str %1 "-" (get %2 row-id))))

           search-bars (search-table data filters-atom removed-hidden-column-defs)

           column-groups (group-columns removed-hidden-column-defs)

           cleaned-data
           (->>
             data
             (filtering-fn)
             (sorting-fn)
             (formatting-fn))

           footer-fns (:footer table-fns)]

       [:div.u-relative
        [:table
         {:class
          (merge
            (yuc/bem :oTable [:has-shadow] :tablesorter)
            (when (:sticky-header? options)
              "u-table-has-sticky-header"))}

         (when colgroup
           colgroup)

         [:thead
          (when column-groups
            [:tr
             (map-indexed
               (fn [idx v] (rum/with-key (group-header-comp v) (str (:group v) "-" idx)))
               column-groups)])
          [:tr
           (map-indexed
             (fn [idx v] (rum/with-key (header-comp sort-title-atom order-atom v) (str (:title v) "-" idx)))
             removed-hidden-column-defs)]]

         (conj
           (into
             [:tbody]
             (concat
               search-bars
               (paging-fn cleaned-data))))

         (cond
           (seq footer-fns)
           [:tfoot
            (into
              [:tr]
              (map
                (fn [column-def]
                  (let [footer-fn (get footer-fns (:key column-def))
                        html-cell-classes
                        (->>
                          [(cond
                             (yuc/presence (:align-class column-def))
                             (:align-class column-def)

                             (contains? #{:currency :number :tocurrency} (:type column-def))
                             :u-text-right

                             (contains? #{:selector :date} (:type column-def))
                             :u-text-center)]
                          (remove nil?)
                          (map name))]

                    [:td
                     {:class html-cell-classes}
                     (cond
                       footer-fn
                       (let [logic-fn (get-logic-fn table-fns column-def)

                             type-fn
                             (case (:type column-def)
                               :number #(yuc/number-format % (:decimals column-def 0))
                               identity)

                             cell-vals
                             (->>
                               (map #(logic-fn app-atom % column-def (get % (:key column-def))) data)
                               (map (fn [cell-val] (type-fn cell-val))))]

                         (footer-fn app-atom column-def cell-vals))

                       :else
                       "")]))
                column-defs))]

           footer-rows
           (into
             [:tfoot]
             (map
               (fn [footer-row]
                 [:tr
                  (map-indexed
                    (fn [idx v] (rum/with-key (footer-comp v) (str (:key v) "-" idx)))
                    footer-row)])
               footer-rows)))]

        (when (:paging? options)
          (pagination/pagination-control pagination-atom {:row-count (count cleaned-data)}))
        (toaster/loading-overlay app-atom table-name)])))

(defn overlay [app-atom action table-name]
  (let [loading? (toaster/is-open? app-atom table-name)]
    (cond
      (and
        loading?
        (string? action))
      (toaster/update-overlay-message app-atom table-name {:text action})

      (and
        (not loading?)
        (= action :open))
      (toaster/open-overlay app-atom table-name)

      (= action :close)
      (toaster/close-overlay app-atom table-name))))

(rum/defc table < rum/reactive [app-atom table-name table-fns]
  (let [table-atom (table-cursor app-atom table-name)
        {:keys [buttons] :as config} (cmn/curact table-atom :config)
        selected-row-ids
        (when table-name
          (cmn/curact table-atom :select))]
    (when config
      [:div
       (dt-buttons/toolbar app-atom table-name selected-row-ids buttons table-fns)
       (table-raw app-atom config table-fns)])))

(defn table-data
  [app-atom
   {:keys [table-name data-source] :as config} data-source-fns]
  (let [parameter-fn (get data-source-fns (:parameter-fn data-source))
        parameters (when parameter-fn (parameter-fn app-atom))]
    (cond
      (:end-point data-source)
      (do
        (overlay app-atom :open table-name)
        (comms/send (:end-point data-source) (or parameters {})))

      (:action data-source)
      (grid/dispatch-action app-atom (:action data-source) (or parameters {}))

      :else
      (toaster/open-toast app-atom :error
        "End point / action not set" {:closetime 5000}))))

(defn close-inline [app-atom table-name selected-row-id]
  (let [table-atom (table-cursor app-atom table-name)
        {:keys [data-source]} (:config @(table-cursor app-atom table-name))
        {:keys [end-point cursor]} data-source
        update-atom
        (cond
          end-point
          (rum/cursor table-atom :data)

          cursor
          (rum/cursor-in app-atom cursor))]
    (swap! table-atom update-in [:config :column-defs]
      (fn [column-defs]
        (mapv
          (fn [col-def]
            (cond
              (:editing? col-def)
              (assoc col-def :editing? false)

              :else col-def))
          column-defs)))
    (swap! update-atom update selected-row-id assoc :editing? false)))

;;Editor could call this directly if there's an auto add feature & is enabled.
(defn add-row [app-atom table-name {:keys [row] :as e}]
  (let [table-atom (table-cursor app-atom)
        {:keys [row-id]} @(rum/cursor-in table-atom [table-name :config])]
    ;;Depending on data-source this swap needs to be updated or error?
    ;;--
    ;;19/04/2024
    ;;Leaving this is for now, to battle test the function below (add-rows)
    ;;This will need to be updated to use that logic for data-source
    (swap! table-atom update-in [table-name :data]
      (fn [data]
        (conj data (yuc/index-by row-id [row]))))))

(defn add-rows [app-atom table-name {:keys [rows] :as e}]
  (let [table-atom (table-cursor app-atom)
        {{:keys [end-point cursor]} :data-source :keys [row-id]}
        @(rum/cursor-in table-atom [table-name :config])

        {:keys [base-atom atom-path]}
        (cond
          end-point
          {:base-atom table-atom :atom-path [table-name :data]}

          cursor
          {:base-atom app-atom :atom-path (if (coll? cursor) cursor [cursor])})]

    (swap! base-atom update-in atom-path
      (fn [data]
        (reduce
          (fn [acc row]
            (assoc acc (get row row-id) row))
          data rows)))))

(defn merge-rows [app-atom table-name {:keys [rows] :as e}]
  (let [table-atom (table-cursor app-atom)
        {{:keys [end-point cursor]} :data-source :keys [row-id]}
        @(rum/cursor-in table-atom [table-name :config])

        {:keys [base-atom atom-path]}
        (cond
          end-point
          {:base-atom table-atom :atom-path [table-name :data]}

          cursor
          {:base-atom app-atom :atom-path (if (coll? cursor) cursor [cursor])})

        current-rows (cmn/curact base-atom atom-path)]

    (reduce
      (fn [acc row]
        (let [row-key (get row row-id)
              current-row (get acc row-key)
              updated-row
              (yuc/maybe
                assoc
                row
                :editing?
                (when (and (yuc/presence (:editing? current-row)) (:editing? current-row))
                  (not (:editing? current-row)))
                :selected?
                (when (and (yuc/presence (:selected? current-row)) (:selected? current-row))
                  (not (:selected? current-row))))]
          (update acc row-key merge updated-row)))
      current-rows rows)))

(defn update-rows [app-atom table-name e]
  (let [table-atom (table-cursor app-atom)
        {{:keys [end-point cursor]} :data-source :keys [row-id]}
        @(rum/cursor-in table-atom [table-name :config])

        {:keys [base-atom atom-path]}
        (cond
          end-point
          {:base-atom table-atom :atom-path [table-name :data]}

          cursor
          {:base-atom app-atom :atom-path (if (coll? cursor) cursor [cursor])})

        updated-rows (merge-rows app-atom table-name e)]

    (cond
      (yuc/presence cursor)
      (do
        (swap! base-atom assoc-in atom-path updated-rows)
        (swap! table-atom update table-name dissoc :select))

      (yuc/presence end-point)
      (swap! table-atom update table-name
        (fn [datatable]
          (->
            datatable
            (assoc
              :data updated-rows
              :select nil))))

      :else
      (toaster/open-toast app-atom :error (str "Data source not found to update for: " table-name) {}))))

(defn remove-row [app-atom table-name e]
  (let [table-atom (table-cursor app-atom)
        selected-rows-atom (rum/cursor-in table-atom [table-name :select])]
    ;;Depending on data-source this swap needs to be updated or error?
    (swap! table-atom update table-name
      (fn [datatable]
        (let [updated-rows
              (reduce
                (fn [acc selected-row-id]
                  (dissoc acc selected-row-id))
                (:data datatable)
                @selected-rows-atom)]
          (->
            datatable
            (assoc :data updated-rows)
            (assoc :select nil)))))))

(defn remove-rows [app-atom table-name {:keys [row-ids] :as e}]
  (let [table-atom (table-cursor app-atom)
        {{:keys [end-point cursor]} :data-source :keys [row-id]}
        @(rum/cursor-in table-atom [table-name :config])

        {:keys [base-atom atom-path]}
        (cond
          end-point
          {:base-atom table-atom :atom-path [table-name :data]}

          cursor
          {:base-atom app-atom :atom-path (if (coll? cursor) cursor [cursor])})

        current-rows (cmn/curact base-atom atom-path)
        updated-rows
        (reduce
          (fn [acc row-id]
            (dissoc acc row-id))
          current-rows
          row-ids)]

    (cond
      (yuc/presence cursor)
      (do
        (swap! base-atom assoc-in atom-path updated-rows)
        (swap! table-atom update table-name dissoc :select))

      (yuc/presence end-point)
      (swap! table-atom update table-name
        (fn [datatable]
          (->
            datatable
            (assoc
              :data updated-rows
              :select nil))))

      :else
      (toaster/open-toast app-atom :error (str "Data source not found to update for: " table-name) {}))))

(defn deselect-rows [app-atom table-name]
  (let [table-atom (table-cursor app-atom)]
    (swap! table-atom update table-name dissoc :select)))

(defn rows [app-atom table-name & [row-ids]]
  (let [table-atom (table-cursor app-atom table-name)
        {:keys [row-id] :as table-cfg} (:config @table-atom)
        data-src (:data-source table-cfg)
        raw-data-atom (data-atom app-atom table-name data-src)
        idx-rows @raw-data-atom]
    (cond
      row-ids
      (yuc/index-by row-id
        (reduce
          (fn [acc row-id]
            (let [row (get idx-rows row-id)]
              (conj acc row)))
          [] row-ids))

      :else
      idx-rows)))

(defn selected-rows [app-atom table-name]
  (let [datatable-atom (table-cursor app-atom table-name)
        selected-ids (cmn/curact datatable-atom :select)]
    (rows app-atom table-name selected-ids)))

(defn filtered-rows [app-atom table-name table-fns]
  (let [table-atom (table-cursor app-atom table-name)
        config-atom (rum/cursor table-atom :config)
        {:keys [row-id column-defs]} @config-atom
        filters (cmn/curact config-atom [:options :filters])
        table-rows (vals (rows app-atom table-name))
        columns-by-title (yuc/index-by #(str/trim (str (:group %) " " (:title %))) column-defs)]
    (yuc/index-by
      row-id
      (filter-rows
        app-atom
        table-fns
        filters
        columns-by-title
        table-rows))))

(defn post-comms [app-atom table-name-key data]
  (let [table-atom (table-cursor app-atom)
        {:keys [table-name row-id] :as table-config}
        @(rum/cursor-in table-atom [table-name-key :config])]
    (toaster/close-overlay app-atom table-name)
    (cond
      table-config
      (add-rows app-atom table-name data)

      :else
      (toaster/open-toast app-atom :error (str "Table config not found: " table-name-key) {}))))

(defn refresh [app-atom table-name table-fns]
  (let [table-atom (table-cursor app-atom table-name)]
    (table-data app-atom (:config @table-atom) (or (:data-source-fns table-fns) {}))))

(defmethod grid/action :datatables/refresh [app-atom config]
  (refresh app-atom (:table-name config) (:table-fns config)))

(defmethod grid/action :datatables/toggle-select-all
  [app-atom {:keys [table-name raw-data-override-fn]} _]
  (let [table-atom (table-cursor app-atom table-name)
        data-source (get-in @table-atom [:config :data-source])
        raw-data-atom (data-atom app-atom table-name data-source)
        raw-data @raw-data-atom

        target-keys
        (if raw-data-override-fn
          (set (keys (raw-data-override-fn raw-data)))
          (set (keys raw-data)))

        new-table-state (swap! table-atom update :select-all-active? not)
        target-val (:select-all-active? new-table-state)]

    (swap! raw-data-atom
      (fn [dataset]
        (reduce
          (fn [m k]
            (if (contains? m k)
              (assoc-in m [k :selected?] target-val)
              m))
          dataset
          target-keys)))

    (swap! table-atom update :select
      (fn [current-selection]
        (let [safe-curr (or current-selection #{})]
          (if target-val
            (set/union safe-curr target-keys)
            (set/difference safe-curr target-keys)))))))

(defn update-config [app-atom table-name new-config]
  (let [table-atom (table-cursor app-atom table-name)]
    (swap! table-atom update :config
      (fn [config]
        (merge config new-config)))))

(defn serverside-rendered []
  #?(:clj true
     :cljs false))

;;Based off of grid/open-modal
(defn initiate
  "Initializes a datatable component with the given configuration.

  Parameters:
  -----------
  app-atom     - The application state atom
  table-config - Configuration map containing:
                - :table-name   - Name identifier for the table
                - :data-source
                  - Data source configuration:
                   - :end-point     - API endpoint for data fetching
                   - :action        - The key for a grid/action. Called in replacement of :end-point with more control
                   - :cursor        - Path to data in app-state
                   - :parameter-fn  - Function to generate API parameters
                - :column-defs  - Vector of column definitions (see below)
  table-fns    - Map of table functions:
                - :data-source-fns - Data source related functions
                - :column-fns      - Column rendering functions
                - :config-fns      - Table configuration functions

  Column Definition Options:
  -------------------------
  :title           - Column header text
  :key            - Key path to data in row (keyword or vector)
  :type           - Data type for formatting:
                    :date          - Date with optional format
                    :currency      - Prefixed with 'R'
                    :percentage    - Appends '%'
                    :tocurrency    - Formatted currency
                    :number        - With optional :decimals
                    :selector      - Checkbox/radio column
                    :link          - Clickable link
                    :action        - Action trigger
                    :img           - Image with Cloudinary support

  Optional Attributes:
  -------------------
  :align-class     - CSS alignment (e.g., :u-text-right)
  :default-hidden? - Hide column by default
  :searchable      - Enable filtering:
                    :select - Dropdown filter
                    true    - Text filter
  :group          - Group columns under header
  :render         - Custom rendering:
                    :render-k - Render function key
                    :logic-k  - Logic function key
  :cell-edit      - Cell editing function key
  :cell-classes-k - Dynamic cell classes key

  Example:
  --------
  {:title \"Date\"
   :type :date
   :key :order/date
   :align-class :u-text-center
   :searchable true
   :render {:render-k :date-formatter}}

  Returns nil."
  [app-atom {:keys [table-name data-source column-defs] :as table-config} table-fns]
  (let [table-atom (table-cursor app-atom table-name)
        idx-columns (index-columns column-defs)]

    (when-not (map? table-config)
      (toaster/open-toast app-atom :error "Datatable config is not a map" {}))

    (when (not @table-atom)
      (println "Initialising table: " table-name)
      (swap! table-atom assoc :config
        (assoc table-config :column-defs idx-columns)))

    (when
      (or
        (and (:end-point data-source) (empty? (:data @table-atom)))
        (and (:action data-source) (:cursor data-source) (empty? (cmn/curact app-atom (:cursor data-source)))))
      (table-data app-atom table-config (:data-source-fns table-fns)))))

(defn reset-data [app-atom {:keys [table-name data-source column-defs] :as table-config} table-fns]
  (let [table-cursor-atom (table-cursor app-atom)
        table-atom (table-cursor app-atom table-name)
        idx-columns (index-columns column-defs)
        serverside-rendered? (serverside-rendered)]
    (cond
      @table-atom
      (do
        (println "Re-initialising table: " table-name)
        (swap! table-cursor-atom update table-name
          (fn [datatable-config]
            (->
              datatable-config
              (assoc :config (assoc table-config :column-defs idx-columns))
              (dissoc :data))))
        (when (:end-point data-source)
          (table-data app-atom table-config (:data-source-fns table-fns))))

      (not serverside-rendered?)
      (initiate app-atom table-config table-fns))))